/**
 * fadeElements
 * 
 * This function observes elements with the classes `.fade-in` and `.fade-up`,
 * and adds the `in-view` class to them when they enter the viewport,
 * triggering CSS-based animations (e.g., fade-in effects).
 * It uses IntersectionObserver for efficiency and MutationObserver to handle
 * dynamically added elements. It also runs once on window load to immediately
 * handle any elements already in view.
 */
export function fadeElements() {
	const addInViewClass = (entries, observer) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				entry.target.classList.add('in-view');
				observer.unobserve(entry.target);
			}
		});
	};

	const intersectionObserver = new IntersectionObserver(addInViewClass, { threshold: 0.1 });

	const observeFadeElements = () => {
		const elements = document.querySelectorAll(".fade-in:not(.in-view), .fade-up:not(.in-view)");
		elements.forEach(el => intersectionObserver.observe(el));
	};

	observeFadeElements();

	const mutationObserver = new MutationObserver(() => {
		observeFadeElements();
	});

	window.addEventListener('load', () => {
		document.querySelectorAll(".fade-in:not(.in-view), .fade-up:not(.in-view)").forEach(el => {
			const rect = el.getBoundingClientRect();
			const inView = rect.top < window.innerHeight && rect.bottom > 0;
			if (inView) {
				el.classList.add('in-view');
			}
		});
	}); 

	mutationObserver.observe(document.body, {
		childList: true,
		subtree: true
	});

}
/**
 * fixedHeaderOffset
 *
 * Returns how much of the top of the viewport a fixed header covers, as the
 * line anchor-link targets land on (theme.js) and the content-sidebar's
 * sidebar pins to - 0 when no header is fixed (e.g. mobile).
 * The solid header (blocks/sticky-header.md) always uses its unscrolled
 * height (the space body.has-header-solid reserves for it), not its current
 * one, so the result doesn't depend on whether .scrolled-header is on yet.
 */
export function fixedHeaderOffset() {
	const header = document.querySelector('#main-header.header-2, #main-header.header-solid');
	if (! header || getComputedStyle(header).position !== 'fixed') return 0;

	return header.classList.contains('header-solid')
		? parseFloat(getComputedStyle(document.body).paddingTop)
		: header.getBoundingClientRect().height
	;
}
